import { useQuery } from '@tanstack/react-query';
import { consoleApi } from '@/lib/consoleApi';
import { AdminPage } from '@/admin';

// Read-only MA visibility for the companion-safety loop. The sign-off target was
// retired (owner ruling 2026-07-23: MA advises, does NOT sign), so companion_safety
// is deliberately absent from the review queue — this page is where the MA sees the
// registry version/status, the live-gate, and the provisional (MA_REQUIRED) gaps,
// with no sign action. Honours "everything the MA needs visibility over lives in
// the console".
export function CompanionSafetyVisibilityPage() {
  const q = useQuery({
    queryKey: ['companion-safety-visibility'],
    queryFn: () => consoleApi.companionSafetyVisibility(),
  });
  const v = q.data;
  const calibrated = v?.live_gate?.detection_calibration_passed ?? false;
  const gaps = v?.provisional_gaps?.unauthored_paths ?? [];

  return (
    <AdminPage
      title="Companion safety — visibility"
      description="Read-only view of the companion-safety loop's registry: version, live-gate, and the provisional data still to be authored. Advisory only — there is no sign-off here."
    >
      {q.isLoading ? <p className="text-body-sm text-text-muted">Loading…</p> : null}
      {q.isError ? <p className="text-body-sm text-flagged">Could not load companion-safety visibility.</p> : null}

      {v ? (
        <div data-testid="cs-visibility">
          <section className="sect">
            <p className="text-body-sm text-text-muted">
              <strong>MA advises, does not sign.</strong> The companion-safety sign-off was retired
              (2026-07-23), so there is nothing to sign here — you can see everything. Detection ships
              only when the engineering calibration gate passes; response wording is composed from the
              clinical corpus, and resource literals (hotlines) are quoted verbatim from the registry,
              never authored by the model.
            </p>
          </section>

          {!v.loaded ? (
            <p className="text-body-sm text-flagged">
              Registry failed to load: <span className="mono">{v.error || 'unknown error'}</span>
            </p>
          ) : null}

          <section className="sect">
            <h2 className="sect-h">Registry</h2>
            <div className="rows">
              <div className="row">
                <div className="grow">
                  <div className="rowtitle">{v.label}</div>
                  <div className="rowsub">
                    <span className="mono">{v.registry}</span> · v{v.version ?? '—'} · status {v.status ?? '—'}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="sect">
            <h2 className="sect-h">Live gate</h2>
            <div className="rows">
              <div className="row">
                <div className="grow">
                  <div className="rowtitle">detection_calibration · {calibrated ? 'passed' : 'not passed'}</div>
                  <div className="rowsub">
                    {calibrated
                      ? 'Calibrated — the companion may emit safety tiers on the live path.'
                      : 'Not calibrated — the loop stays on the deterministic floor (real hotlines). Flipped by the battery/CI, never by hand.'}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="sect">
            <h2 className="sect-h">Provisional gaps <span>({gaps.length})</span></h2>
            {v.provisional_gaps?.error ? (
              <p className="text-body-sm text-flagged">
                Could not read provisional gaps: <span className="mono">{v.provisional_gaps.error}</span>
              </p>
            ) : v.provisional_gaps?.authored ? (
              <p className="empty">Fully authored — no provisional resource literals or detection content pending.</p>
            ) : (
              <div className="rows">
                {gaps.map((p) => (
                  <div className="row" key={p}>
                    <div className="grow">
                      <div className="rowtitle mono">{p}</div>
                      <div className="rowsub">
                        Provisional — ships MA_REQUIRED, renders inert until authored (fail-closed).
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      ) : null}
    </AdminPage>
  );
}
